<template>
    <div class="user member-notification-page camp-member-page">
        <div class="headerbg">
            <b-container class="d-flex align-items-center justify-content-between">
                <h1>通知中心</h1>
                <b-button variant="outline-secondary" size="sm" @click="logout">登出</b-button>
            </b-container>
        </div>
        <b-container fluid="xl">
            <b-row>
                <b-col cols="12" md="2" class="member-sidebar-col"><nacUser /></b-col>
                <b-col cols="12" md="10" class="member-content-col">
                    <b-card class="mb-3">
                        <template #header>
                            <div class="d-flex flex-wrap justify-content-between align-items-center">
                                <span class="font-weight-bold">
                                    我的通知
                                    <b-badge v-if="unreadCount" pill variant="danger" class="ml-1">{{ unreadCount }}</b-badge>
                                </span>
                                <b-button size="sm" variant="outline-primary" :disabled="loading || unreadCount === 0" @click="readAll">
                                    <b-icon icon="check2-all" class="mr-1"></b-icon>全部已讀
                                </b-button>
                            </div>
                        </template>

                        <b-row class="mb-3" align-v="end">
                            <b-col cols="6" md="3">
                                <b-form-group label="類型" class="mb-0">
                                    <b-form-select v-model="filters.type" :options="typeOptions" />
                                </b-form-group>
                            </b-col>
                            <b-col cols="6" md="2">
                                <b-form-group label="狀態" class="mb-0">
                                    <b-form-select v-model="filters.read" :options="readOptions" />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="5" class="mt-2 mt-md-0">
                                <b-form-group label="關鍵字" class="mb-0">
                                    <b-form-input v-model.trim="filters.keywords" placeholder="搜尋標題或內容" @keyup.enter="search" />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="2" class="mt-2 mt-md-0">
                                <b-button variant="primary" block @click="search">搜尋</b-button>
                            </b-col>
                        </b-row>

                        <div class="notification-actions d-flex justify-content-between align-items-center mb-2">
                            <b-form-checkbox v-model="selectPage" :disabled="items.length === 0">
                                <span class="notification-action-full">選取本頁</span>
                                <span class="notification-action-short">本頁</span>
                            </b-form-checkbox>
                            <div class="notification-actions-main d-flex align-items-center">
                                <b-dropdown size="sm" variant="outline-secondary" right>
                                    <template #button-content>
                                        <b-icon icon="filter-left" class="mr-1"></b-icon>
                                        <span class="notification-action-full">{{ currentSortText }}</span>
                                        <span class="notification-action-short">排序</span>
                                    </template>
                                    <b-dropdown-item v-for="option in sortOptions" :key="option.value" @click="changeSort(option.value)">
                                        {{ option.text }}
                                    </b-dropdown-item>
                                </b-dropdown>
                                <b-button size="sm" variant="outline-secondary" :disabled="selectedIds.length === 0" @click="readSelected">
                                    <b-icon icon="check2-square" class="mr-1"></b-icon>
                                    <span class="notification-action-full">標示已選已讀</span>
                                    <span class="notification-action-short">已讀</span>
                                </b-button>
                            </div>
                        </div>

                        <div v-if="loading" class="text-center text-muted py-5">載入中...</div>
                        <div v-else-if="items.length === 0" class="text-center text-muted py-5">目前沒有通知</div>
                        <div v-else class="notification-list">
                            <article v-for="item in items" :key="item.Id" class="notification-item" :class="{ unread: !item.IsRead }">
                                <b-form-checkbox v-model="selectedIds" :value="item.Id" class="notification-check" />
                                <div class="notification-type">
                                    <b-badge variant="info">{{ typeText(item.Type) }}</b-badge>
                                    <span v-if="!item.IsRead" class="unread-label">未讀</span>
                                </div>
                                <button type="button" class="notification-content" @click="openDetail(item.Id)">
                                    <h2>{{ item.Title }}</h2>
                                    <p>{{ item.ContentSummary }}</p>
                                </button>
                                <time class="notification-date small text-muted">{{ formatDate(item.PublishedAt) }}</time>
                            </article>
                        </div>

                        <div class="d-flex flex-wrap justify-content-between align-items-center mt-3">
                            <span class="small text-muted">共 {{ paging.totalCount }} 筆</span>
                            <b-pagination v-model="paging.pageNumber" :total-rows="paging.totalCount" :per-page="paging.pageSize" class="mb-0" @change="changePage" />
                        </div>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import moment from 'moment';
import nacUser from '@/components/nacUser.vue';
import {
    apiGetMemberNotificationList,
    apiReadAllMemberNotifications,
    apiReadMemberNotifications,
} from '@/api/API';
import { MemberNotificationTypeOptions, memberNotificationTypeText } from '@/utils/memberNotification';
import memberLoginContinuation from '@/mixins/memberLoginContinuation';

export default {
    name: 'MemberNotification',
    mixins: [memberLoginContinuation],
    components: { nacUser },
    data() {
        return {
            loading: false,
            items: [],
            selectedIds: [],
            filters: { keywords: '', type: '', read: '' },
            includeHistory: false,
            sort: 'publishedAt:desc',
            paging: { pageNumber: 1, pageSize: 10, totalCount: 0 },
            typeOptions: MemberNotificationTypeOptions,
            readOptions: [
                { value: '', text: '全部' },
                { value: 'unread', text: '未讀' },
                { value: 'read', text: '已讀' },
            ],
            sortOptions: [
                { value: 'publishedAt:desc', text: '發布時間（新到舊）' },
                { value: 'publishedAt:asc', text: '發布時間（舊到新）' },
            ],
        };
    },
    computed: {
        unreadCount() { return this.$store.state.memberNotification.unreadCount; },
        currentSortText() {
            return this.sortOptions.find((option) => option.value === this.sort)?.text || '排序';
        },
        selectPage: {
            get() { return this.items.length > 0 && this.items.every((item) => this.selectedIds.includes(item.Id)); },
            set(selected) { this.selectedIds = selected ? this.items.map((item) => item.Id) : []; },
        },
    },
    mounted() {
        if (this.IsNull(this._sessionData('TokenID')) === '') {
            this.requestMemberLogin(this.loadList);
            return;
        }
        this.loadList();
    },
    methods: {
        async loadList() {
            this.loading = true;
            try {
                const search = {};
                Object.keys(this.filters).forEach((key) => {
                    if (this.filters[key]) search[key] = this.filters[key];
                });
                const sortParts = this.sort.split(':');
                const sort = { [sortParts[0]]: sortParts[1] };
                const response = await apiGetMemberNotificationList({
                    Paging: { pageNumber: this.paging.pageNumber, pageSize: this.paging.pageSize },
                    Search: search,
                    Sort: sort,
                    IncludeHistory: this.includeHistory,
                });
                this.items = response.data || [];
                this.selectedIds = [];
                const header = response.headers['list-header'];
                if (header) this.paging.totalCount = JSON.parse(header).Paging.totalCount;
            } finally {
                this.loading = false;
            }
        },
        search() { this.paging.pageNumber = 1; this.loadList(); },
        changeSort(value) {
            if (this.sort === value) return;
            this.sort = value;
            this.search();
        },
        changePage(page) { this.paging.pageNumber = page; this.loadList(); },
        openDetail(id) { this._go(`/memberNotification/${id}`); },
        async readSelected() {
            await apiReadMemberNotifications(this.selectedIds);
            await this.refreshAfterRead();
        },
        async readAll() {
            await apiReadAllMemberNotifications();
            await this.refreshAfterRead();
        },
        async refreshAfterRead() {
            await Promise.all([this.loadList(), this.$store.dispatch('memberNotification/fetchUnreadCount')]);
        },
        typeText: memberNotificationTypeText,
        formatDate(value) { return value ? moment(value).format('YYYY/MM/DD HH:mm') : '-'; },
        logout() {
            this._sessionData('NewToken', '');
            this._sessionData('TokenID', '');
            this._go('/');
        },
    },
};
</script>

<style scoped>
.notification-item { display: grid; grid-template-columns: auto minmax(8rem, 12rem) minmax(0, 1fr) auto; gap: 0.75rem; align-items: center; border-top: 1px solid #e9ecef; padding: 0.9rem 0.75rem; }
.notification-item.unread { background: #f3f9ff; border-left: 3px solid #1677c8; }
.notification-check { margin: 0; }
.notification-type { display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem; }
.notification-content { display: grid; grid-template-columns: minmax(10rem, 0.8fr) minmax(0, 1.2fr); gap: 0.75rem; align-items: center; min-width: 0; padding: 0; border: 0; background: transparent; color: inherit; text-align: left; }
.notification-content h2 { margin: 0; font-size: 1rem; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.notification-content p { margin: 0; color: #5f6872; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.notification-date { white-space: nowrap; }
.unread-label { color: #0b69a3; font-size: 0.8rem; font-weight: 600; }
.notification-actions { gap: 0.75rem; border: 1px solid #dbe7ef; border-radius: 0.35rem; padding: 0.65rem 0.75rem; background: #f5faff; }
.notification-actions-main { gap: 0.5rem; }
.notification-action-short { display: none; }

@media (max-width: 767.98px) {
    .notification-item { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 0.5rem 0.75rem; margin-bottom: 0.75rem; border: 1px solid #e9ecef; border-radius: 0.35rem; padding: 0.9rem; }
    .notification-item.unread { border-left: 3px solid #1677c8; }
    .notification-type { grid-column: 2; grid-row: 1; }
    .notification-date { grid-column: 3; grid-row: 1; }
    .notification-content { display: block; }
    .notification-content { grid-column: 2 / 4; grid-row: 2; }
    .notification-content h2 { margin-bottom: 0.25rem; white-space: normal; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; }
    .notification-content p { white-space: normal; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
}

@media (max-width: 529.98px) {
    .notification-actions { gap: 0.5rem; padding: 0.55rem; }
    .notification-actions-main { gap: 0.35rem; }
    .notification-actions .btn { white-space: nowrap; }
    .notification-action-full { display: none; }
    .notification-action-short { display: inline; }
}
</style>
